import { Box, ButtonBase, Typography } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import { useBetslipCount, useBetslipInfoMessageKeys } from '../../../../stores/betslip/selectors';
import useLocalization from '../../../../hooks/useLocalization';
import { useBetslipFlyAnimation } from '../../../../hooks/useBetslipFlyAnimation';
import { styles } from './Header.styles';
import { Article } from '@mui/icons-material';

type MobileBetslipFabProps = {
  openBetslip: () => void;
};

const EXIT_ANIMATION_MS = 220;
type CountAnimationMode = 'add' | 'remove' | null;

const MobileBetslipFab = ({ openBetslip }: MobileBetslipFabProps) => {
  const { t } = useLocalization();
  const betslipCount = useBetslipCount();
  const infoMessageKeys = useBetslipInfoMessageKeys();
  const previousCountRef = useRef(betslipCount);
  const [isExiting, setIsExiting] = useState(false);
  const [displayCount, setDisplayCount] = useState(betslipCount);
  const [countAnimationKey, setCountAnimationKey] = useState(0);
  const [countAnimationMode, setCountAnimationMode] = useState<CountAnimationMode>(null);
  const { badgeRef, flyAnimation } = useBetslipFlyAnimation(betslipCount);

  useLayoutEffect(() => {
    const previousCount = previousCountRef.current;

    if (betslipCount > 0) {
      setIsExiting(false);
      setDisplayCount(betslipCount);

      if (betslipCount !== previousCount) {
        const nextAnimationKey = countAnimationKey + 1;
        const isAddingBet = betslipCount > previousCount;

        setCountAnimationKey(nextAnimationKey);
        setCountAnimationMode(isAddingBet ? 'add' : 'remove');
      }

      previousCountRef.current = betslipCount;

      return undefined;
    }

    previousCountRef.current = 0;

    if (!previousCount) {
      return undefined;
    }

    setIsExiting(true);

    // Keep the final badge count mounted long enough for the FAB exit animation to finish.
    const timeout = window.setTimeout(() => {
      setDisplayCount(0);
      setIsExiting(false);
    }, EXIT_ANIMATION_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [betslipCount, countAnimationKey]);

  const shouldRender = displayCount > 0 || betslipCount > 0 || isExiting;
  const isVisible = betslipCount > 0;
  const animateEntrance = betslipCount > 0 && previousCountRef.current === 0;

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <ButtonBase
        aria-label={t('betslip', 'Betslip')}
        sx={styles.mobileBetslipFab(isVisible, animateEntrance)}
        onClick={openBetslip}
        data-testid='neobet-mobile-betslip-fab'
      >
        {infoMessageKeys.size > 0 ? (
          <Box sx={styles.mobileInfoMessageBubble} data-testid='neobet-mobile-betslip-info-tray'>
            {Array.from(infoMessageKeys).map((messageKey) => (
              <Typography key={messageKey} sx={styles.mobileInfoMessage}>
                {t(messageKey)}
              </Typography>
            ))}
          </Box>
        ) : null}
        <Article />
        <Box ref={badgeRef} key={`badge-${countAnimationKey}`} sx={styles.mobileBetslipFabBadge(countAnimationMode)}>
          {displayCount || betslipCount}
        </Box>
      </ButtonBase>
      {flyAnimation ? (
        <Box
          key={`fly-${flyAnimation.key}`}
          sx={styles.mobileBetslipFabFlyDot(flyAnimation.origin, flyAnimation.target)}
          data-testid='neobet-mobile-betslip-fly-dot'
        />
      ) : null}
    </>
  );
};

export default MobileBetslipFab;
